import { FileUploader } from "@/components/molecules/FileUploader"
import type { FileWithPath } from "@/components/molecules/FileUploader/FileUploader.types"
import { Input } from "@/components/molecules/Input"
import { RichTextEditor } from "@/components/molecules/RichTextEditor"
import { Select } from "@/components/molecules/Select"
import { Textarea } from "@/components/molecules/Textarea"
import type { MentoringDataType } from "@/types/mentoring"
import { useEffect, useState } from "react"
import { currencyMask, dateMask } from "@/utils/masks"
import { formatToBr } from "@/utils/formatDateTime"
import { Company } from "@/types/company"
import { useToast } from "@/hooks/useToast"
import { useAuth } from "@/contexts/AuthContext"
import { useParams } from "react-router-dom"

interface MentoringsFormProps {
  initialData?: Partial<MentoringDataType>
  onSubmit: (data: Partial<MentoringDataType>, file: File | null) => void
  companyData?: Company | null
}

const unmaskCurrency = (value: string | number): string => {
  if (typeof value === "number") {
    return String(value)
  }
  return value.replace("R$", "").replace(/\./g, "").replace(",", ".").trim()
}

const parseBrDateToISO = (dateString: string, timeString: string): string => {
  try {
    const [day, month, year] = dateString.split("/")
    const [hours, minutes] = timeString.split(":")
    const date = new Date(`${year}-${month}-${day}T${hours || "00"}:${minutes || "00"}:00`)
    if (isNaN(date.getTime())) {
      return dateString
    }
    return date.toISOString()
  } catch (error) {
    console.error("Erro ao fazer parse da data:", error)
    return dateString
  }
}

export const MentoringsForm = ({ initialData, onSubmit, companyData }: MentoringsFormProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [formData, setFormData] = useState<Partial<MentoringDataType>>(
    initialData || { tipoMentoria: 1 }
  )
  const [dateError, setDateError] = useState<string | null>(null)
  const [time, setTime] = useState("")
  const { user } = useAuth()
  const showToast = useToast()
  const { companyId } = useParams()

  useEffect(() => {
    if (initialData) {
      const date = initialData.dataHoraMentoria
        ? formatToBr(initialData.dataHoraMentoria, { formatType: "date" })
        : ""
      const timeValue = initialData.dataHoraMentoria
        ? formatToBr(initialData.dataHoraMentoria, { formatType: "time" })
        : ""
      setFormData({
        ...initialData,
        dataHoraMentoria: date,
        valorMentoria: initialData.valorMentoria ? currencyMask(initialData.valorMentoria) : "",
      })
      setTime(timeValue)
    }
  }, [initialData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target

    if (id === "valorMentoria") {
      const maskedValue = currencyMask(value)
      setFormData((prev) => ({ ...prev, [id]: maskedValue }))
    } else {
      setFormData((prev) => ({ ...prev, [id]: value }))
    }
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const maskedValue = dateMask(value)

    if (maskedValue.length > 0 && maskedValue.length < 10) {
      setDateError("Data inválida.")
    } else {
      setDateError(null)
    }

    setFormData((prev) => ({ ...prev, dataHoraMentoria: maskedValue }))
  }

  // const handleCopyLink = () => {
  //   if (formData.linkMentoria) {
  //     navigator.clipboard.writeText(formData.linkMentoria)
  //     showToast({
  //       message: "Link copiado para a área de transferência!",
  //       variant: "success",
  //     })
  //   }
  // }

  // const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const { value } = e.target
  //   const maskedValue = timeMask(value)
  //   setTime(maskedValue)
  // }

  const handleSelectChange = (id: string, value: string) => {
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  const handleEditorChange = (id: string, value: string) => {
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const dataToSubmit = { ...formData, originador_id: user.pes_id, empId: companyData?.emp_id }

    if (dataToSubmit.valorMentoria) {
      dataToSubmit.valorMentoria = unmaskCurrency(dataToSubmit.valorMentoria)
    }

    if (dataToSubmit.dataHoraMentoria) {
      dataToSubmit.dataHoraMentoria = parseBrDateToISO(dataToSubmit.dataHoraMentoria, time)
    }

    onSubmit(dataToSubmit, file)
  }

  return (
    <form
      id="mentoring-form"
      onSubmit={handleSubmit}
      className="space-y-6 overflow-auto"
    >
      <Input
        id="temaMentoria"
        label="Tema da seção"
        placeholder="Digite o tema"
        value={formData.temaMentoria || ""}
        onChange={handleChange}
      />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3">
          <Input
            id="dataHoraMentoria"
            label="Data da Mentoria"
            placeholder="DD/MM/AAAA"
            value={formData.dataHoraMentoria || ""}
            onChange={handleDateChange}
            maxLength={10}
            disabled={formData.statusMentoria !== 3 && formData.mentoriaId !== undefined}
            state={dateError ? "error" : "default"}
            helperText={dateError}
          />
        </div>
        {/* <div className="col-span-3">
          <Input
            id="hora_mentoria"
            label="Hora da Mentoria"
            placeholder="HH:MM"
            value={time}
            disabled={formData.statusMentoria !== 3 && formData.mentoriaId !== undefined}
            onChange={handleTimeChange}
            maxLength={5}
          />
        </div>
        <div className="col-span-3">
          <Input
            id="valorMentoria"
            label="Valor R$"
            placeholder="R$ 0,00"
            value={formData.valorMentoria || ""}
            onChange={handleChange}
          />
        </div> */}
        <div className="col-span-3">
          <Select
            id="tipoMentoria"
            label="Tipo Mentoria"
            placeholder="Selecione o tipo"
            value={String(formData.tipoMentoria || null)}
            onValueChange={(value) => handleSelectChange("tipoMentoria", value)}
            disabled={formData.statusMentoria !== 3}
            options={[
              { value: "1", label: "Presencial" },
              { value: "2", label: "Online" },
            ]}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-6">
          <Input
            id="participantesMentoria"
            label="Participantes"
            placeholder="Digite os nomes"
            value={formData.participantesMentoria || ""}
            onChange={handleChange}
          />
        </div>
        {/* <div className="col-span-6 flex items-end gap-2">
          <Input
            id="linkMentoria"
            label="Link da gravação"
            iconRight="copy"
            onIconClick={handleCopyLink}
            placeholder="Cole o link aqui"
            readOnly={formData.statusMentoria !== 3 && formData.mentoriaId !== undefined}
            value={formData.linkMentoria || ""}
            onChange={handleChange}
          />
        </div> */}
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <RichTextEditor
            id="atividadesMentoria"
            onChange={(value) => handleEditorChange("atividadesMentoria", value)}
            value={formData.atividadesMentoria || ""}
            label="Atividades e tarefas"
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-6">
          <Textarea
            id="descricaoMentoria"
            label="Resumo da seção"
            placeholder="Digite o resumo"
            value={formData.descricaoMentoria || ""}
            onChange={handleChange}
          />
        </div>
        <div className="col-span-6 flex flex-col gap-[4px]">
          <label
            className="text-[15px] font-bold text-dark-100"
            htmlFor="documento"
          >
            Upload de Arquivo
          </label>
          <FileUploader
            initialFile={formData.documento || null}
            onView={(file) => {
              const fileWithPath = file as FileWithPath
              if (fileWithPath.path) {
                window.open(fileWithPath.path, "_blank")
              } else if (file instanceof File) {
                const url = URL.createObjectURL(file)
                window.open(url, "_blank")
              }
            }}
            onFileSelect={setFile}
            id="documento"
            label="Upload de documento"
          />
        </div>
      </div>
    </form>
  )
}
